import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { PlatformIcon } from '@/components/primitives/PlatformIcon';
import styles from './PlatformSwitcher.module.css';

export interface PlatformSwitcherOption {
  name: string;
  /** Muted figure on the right of the row, e.g. "337.3M". */
  meta?: string;
}

export interface PlatformSwitcherProps {
  /** Current platform — also the heading text. */
  value: string;
  /** In display order (the profile passes them ranked by subscribers). */
  options: PlatformSwitcherOption[];
  /** Heading over the ranked list, e.g. "By subscribers". */
  listLabel?: string;
  onChange: (name: string) => void;
}

/**
 * The profile heading that switches platform, after d360: the name is the
 * trigger; the list opens under it with a search box, the current platform
 * pinned, then the rest. The page dims behind it so the choice has focus.
 *
 * Portalled to <body> so the hero's overflow can't clip it. While open, a
 * replica of the heading sits above the dim at the trigger's exact position,
 * so the name stays lit and clicking it closes the list.
 */
export function PlatformSwitcher({ value, options, listLabel = 'Platforms', onChange }: PlatformSwitcherProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const others = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.name !== value && (!q || o.name.toLowerCase().includes(q)));
  }, [options, value, query]);
  const current = options.find((o) => o.name === value);
  const showCurrent = !!current && (!query.trim() || value.toLowerCase().includes(query.trim().toLowerCase()));
  const rows = showCurrent && current ? [current, ...others] : others;

  const close = () => {
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  };

  const choose = (name: string) => {
    setOpen(false);
    setQuery('');
    if (name !== value) onChange(name);
  };

  // Measure the trigger on open and keep the panel attached on resize/scroll.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => triggerRef.current && setRect(triggerRef.current.getBoundingClientRect());
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Reset the highlight to the first row whenever the list changes.
  useEffect(() => setActive(0), [query, open]);

  // Keep the highlighted row in view while arrowing.
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(rows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && rows[active]) {
      e.preventDefault();
      choose(rows[active].name);
    }
  };

  const row = (o: PlatformSwitcherOption, index: number) => (
    <button
      key={o.name}
      type="button"
      role="option"
      aria-selected={o.name === value}
      data-index={index}
      data-active={index === active || undefined}
      data-current={o.name === value || undefined}
      className={styles.option}
      onMouseEnter={() => setActive(index)}
      onClick={() => choose(o.name)}
    >
      <PlatformIcon name={o.name} size={24} className={styles.optionIcon} />
      <span className={styles.optionName}>{o.name}</span>
      {o.meta && <span className={styles.optionMeta}>{o.meta}</span>}
    </button>
  );

  const heading = (
    <>
      <span className={styles.label}>{value}</span>
      <ChevronDown size={24} className={styles.chevron} aria-hidden="true" />
    </>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        title="Switch platform"
        style={open ? { visibility: 'hidden' } : undefined}
      >
        {heading}
      </button>

      {open &&
        rect &&
        createPortal(
          <div className={styles.layer} onKeyDown={onKeyDown}>
            <div className={styles.scrim} onClick={close} aria-hidden="true" />

            {/* Lit replica of the heading, exactly over the real one */}
            <button
              type="button"
              className={styles.trigger}
              data-open
              style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
              onClick={close}
              aria-label="Close platform list"
            >
              {heading}
            </button>

            <div
              className={styles.panel}
              style={{
                top: rect.bottom + 8,
                left: rect.left,
                maxHeight: `min(460px, calc(100vh - ${Math.round(rect.bottom) + 24}px))`,
              }}
              role="dialog"
              aria-label="Switch platform"
            >
              <label className={styles.search}>
                <Search size={16} className={styles.searchIcon} aria-hidden="true" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for platforms"
                  className={styles.searchInput}
                  aria-label="Search for platforms"
                  aria-controls="platform-switcher-list"
                />
              </label>

              <div ref={listRef} id="platform-switcher-list" role="listbox" className={styles.list}>
                {rows.length === 0 && <p className={styles.empty}>No platforms match “{query}”</p>}
                {showCurrent && current && (
                  <>
                    <div className={styles.section}>Current</div>
                    {row(current, 0)}
                  </>
                )}
                {others.length > 0 && <div className={styles.section}>{listLabel}</div>}
                {others.map((o, i) => row(o, i + (showCurrent ? 1 : 0)))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
